import { useState, useEffect, useCallback, useMemo } from 'react';

interface UseAuthReturn {
  isLoggedIn: boolean;
  user: any | null;
  role: string;
  loading: boolean;
  login: (userData: any, token: string, userRole: string) => void;
  logout: () => void;
  checkAuthStatus: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<any | null>(null);
  const [role, setRole] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  const login = useCallback((userData: any, token: string, userRole: string): void => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('role', userRole);

    setUser(userData);
    setRole(userRole);
    setIsLoggedIn(true);
  }, []);

  const logout = useCallback((): void => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');

    setUser(null);
    setRole('');
    setIsLoggedIn(false);
  }, []);

  const checkAuthStatus = useCallback((): void => {
    const token = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');
    const storedRole = localStorage.getItem('role');

    if (token && storedUser && storedRole) {
      try {
        const parsedUser = JSON.parse(storedUser) as any;
        setUser(parsedUser);
        setRole(storedRole);
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        setIsLoggedIn(false);
      }
    } else {
      setIsLoggedIn(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const authValue = useMemo<UseAuthReturn>(() => ({
    isLoggedIn,
    user,
    role,
    loading,
    login,
    logout,
    checkAuthStatus
  }), [isLoggedIn, user, role, loading, login, logout, checkAuthStatus]);

  return authValue;
};
